import { useEffect, useRef } from 'react';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { FUNC_NOOP } from '@libs/constants';
import { SocketInternalEvents } from '@libs/socket-signalr';

import { IGame } from '@features/game/types/game';
import { gameStructuralSharing } from '@features/game/utils/gameStructuralSharing';

import { COMMON_QUERY_KEYS } from '../commonQueryKeys';
import {
  components,
  GET,
  paths,
  RoundShortGamesSocket,
  RoundShortGamesSocketEvents,
} from './clients';

/*
    ____,-------------------------------,____
    \   |             REST              |   /
    /___|-------------------------------|___\
*/

export function getTournamentPgnLink(tournamentId: string, userId?: string) {
  const TOURNAMENT = '{tournamentId}';
  let path: keyof paths = `/api/v1/stream/tournaments/${TOURNAMENT}/pgn`;

  if (userId) {
    path += `?userId=${userId}`;
  }

  return `${__RUNTIME.clubApi}${path.replace(TOURNAMENT, tournamentId)}`;
}

export function getRoundPgnLink(tournamentId: string, roundNumber: number) {
  const TORUNAMENT = '{tournamentId}';
  const ROUND = '{roundNumber}';
  const path: keyof paths = `/api/v1/stream/tournaments/${TORUNAMENT}/rounds/${ROUND}/pgn`;

  return `${__RUNTIME.clubApi}${path.replace(TORUNAMENT, tournamentId).replace(ROUND, roundNumber.toString())}`;
}

export function getGamePgnLink(gameId: string) {
  const GAME = '{gameId}';
  const path: keyof paths = `/api/v1/stream/games/${GAME}/pgn`;

  return `${__RUNTIME.clubApi}${path.replace(GAME, gameId)}`;
}

export function useStreamsGameQuery(
  id: string,
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    enabled,
    refetchOnWindowFocus: false,
    staleTime,
    queryKey: COMMON_QUERY_KEYS.game(id),
    queryFn: async () => {
      return (
        await GET('/api/v1/stream/games/{gameId}', {
          params: { path: { gameId: id } },
        })
      ).data! as IGame;
    },
    structuralSharing: gameStructuralSharing,
  });
}

export function useStreamsGameShortQuery(
  id: string,
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    enabled,
    refetchOnWindowFocus: false,
    staleTime,
    queryKey: COMMON_QUERY_KEYS.gameShort(id),
    queryFn: async () => {
      return (
        await GET('/api/v1/stream/games/{gameId}/short', {
          params: { path: { gameId: id } },
        })
      ).data!;
    },
  });
}

export function useStreamRoundQuery(
  tournamentId: string,
  roundId: string,
  {
    enabled = true,
    refetchOnWindowFocus = false,
  }: { enabled?: boolean; refetchOnWindowFocus?: boolean } = {},
) {
  return useQuery({
    enabled,
    queryKey: COMMON_QUERY_KEYS.roundGames(tournamentId, roundId),
    queryFn: async () => {
      return (
        await GET('/api/v1/stream/tournaments/{tournamentId}/rounds/{roundId}/games', {
          params: { path: { tournamentId, roundId } },
        })
      ).data!;
    },
    refetchOnWindowFocus,
  });
}

// Опции запроса: мультикомната судьи грузит партии туров нескольких турниров через useQueries
export function roundShortGamesQueryOptions(tournamentId: string, roundNumber: number) {
  return {
    queryKey: COMMON_QUERY_KEYS.roundShortGames(tournamentId, roundNumber),
    queryFn: async () => {
      return (
        await GET('/api/v2/stream/tournaments/{tournamentId}/rounds/{roundId}/games', {
          params: { path: { tournamentId, roundId: roundNumber.toString() } },
        })
      ).data!;
    },
  };
}

export function useRoundShortGamesQuery(
  tournamentId: string,
  roundNumber: number,
  {
    enabled = true,
    refetchOnWindowFocus = false,
  }: { enabled?: boolean; refetchOnWindowFocus?: boolean } = {},
) {
  return useQuery({
    enabled,
    refetchOnWindowFocus,
    ...roundShortGamesQueryOptions(tournamentId, roundNumber),
  });
}

/*
    ____,-------------------------------,____
    \   |            Sockets            |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// Round Short Games

export type RoundShortGamesEvents =
  | RoundShortGamesSocketEvents
  | SocketInternalEvents
  // HACK: синтетическое событие, если в раунд добавились новые партии (доп.
  // партии)
  // FIXME: удалить, когда на сервер реализует нормальное событие (игра
  // создана) + начнёт возвращать NotStarted партии в ручках с результатами,
  // либо когда для всех доп. партий будет создаваться отдельный раунд
  | { eventType: 'NewGameStarted' };

type RoundShortGamesWsContext = {
  games?: Record<string, components['schemas']['GameStreamStateShort']>;
  allGamesFinished: boolean;
};

const globalRoundWs: Record<string, RoundShortGamesSotre> = {};

export function useRoundShortGamesSocket({
  tournamentId,
  roundNumber,
  cb,
  enabled = true,
}: {
  tournamentId: string;
  roundNumber: number;
  cb: (e: RoundShortGamesEvents, ctx: RoundShortGamesWsContext) => void;
  customSocketUrl?: string;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();

  const ref = useRef(cb);
  ref.current = cb;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const key = `${tournamentId}/${roundNumber}`;

    if (!globalRoundWs[key]) {
      globalRoundWs[key] = new RoundShortGamesSotre(
        new RoundShortGamesSocket(tournamentId, roundNumber),
        queryClient,
      );
    }

    const store = globalRoundWs[key];
    const unsubscribe = store.ws.subscribe((e) => {
      const ctx = {
        games: store.games,
        allGamesFinished: !store.hasUnfinishedGames,
      };

      const ids = new Set();

      if (e.eventType === 'RoundGamesStateShort') {
        e.data.forEach((g) => ids.add(g.gameId));
      } else if (e.eventType === 'GameChangedShort') {
        // HACK: проверка статуса нужна т.к. в ручки результатов турнира (для
        // которых нужно это событие) не прилетают NotStarted партии
        if (!ids.has(e.gameId) && e.currentState.status === 'InProgress') {
          ids.add(e.gameId);
          ref.current({ eventType: 'NewGameStarted' }, ctx);
        }
      }

      ref.current(e, ctx);
    });

    // отправляем снэпшот
    const snapshot = store.snapshot;
    if (snapshot) {
      const ctx = {
        games: store.games,
        allGamesFinished: !store.hasUnfinishedGames,
      };
      ref.current(snapshot, ctx);
    }

    return () => {
      unsubscribe();
      if (store.subscribers === 0) {
        store.discard();
        delete globalRoundWs[key];
      }
    };
  }, [tournamentId, roundNumber, enabled, queryClient]);
}

// Сокеты партий сразу нескольких туров (мультикомната судьи): только чтобы стор обновлял
// кэш запросов, события никому не нужны. Хук по одному туру нельзя вызывать в цикле
export function useRoundShortGamesSockets(rounds: { tournamentId: string; roundNumber: number }[]) {
  const queryClient = useQueryClient();
  const key = rounds
    .filter((r) => r.roundNumber > 0)
    .map((r) => `${r.tournamentId}/${r.roundNumber}`)
    .join(',');

  useEffect(() => {
    const releases = key.split(',').filter(Boolean).map((k) => {
      const at = k.lastIndexOf('/');
      return holdRoundStore(k.slice(0, at), Number(k.slice(at + 1)), queryClient);
    });
    return () => releases.forEach((release) => release());
  }, [key, queryClient]);
}

// Стор нужен, чтобы хранить и обновлять snapshot
class RoundShortGamesSotre {
  games: Record<string, components['schemas']['GameStreamStateShort']> | undefined;
  snapshot: components['schemas']['RoundGamesStateShort'] | undefined;
  hasUnfinishedGames: boolean = true;
  ws: RoundShortGamesSocket;
  #ql: QueryClient;

  constructor(ws: RoundShortGamesSocket, ql: QueryClient) {
    this.ws = ws;
    this.#ql = ql;
    ws.subscribe((e) => {
      switch (e.eventType) {
        case 'RoundGamesStateShort':
          this.games = {};

          e.data.forEach((g) => {
            this.games![g.gameId] = g;
            // кэш игры
            this.#ql.setQueryData(COMMON_QUERY_KEYS.gameShort(g.gameId), g);
          });

          // кэш раунда
          this.#ql.setQueryData(
            COMMON_QUERY_KEYS.roundShortGames(this.ws.tournamentId, this.ws.roundNumber),
            e.data,
          );

          this.#update();
          break;
        case 'GameChangedShort':
          if (this.games) {
            this.games[e.gameId] = e.currentState;
          }

          // кэш игры
          this.#ql.setQueryData(COMMON_QUERY_KEYS.gameShort(e.gameId), e.currentState);

          // кэш раунда
          if (this.games) {
            this.#ql.setQueryData(
              COMMON_QUERY_KEYS.roundShortGames(this.ws.tournamentId, this.ws.roundNumber),
              Object.values(this.games),
            );
          }

          this.#update();
          break;
      }

      // SIC! Нельзя отключать сокет по завершении всех игр, т.к. есть турниры,
      // в которые игры добавляются динамически
      // if (!this.ws.discarded && !this.hasUnfinishedGames) {
      //   // даём обработать другие подписки
      //   requestAnimationFrame(() => this.discard());
      // }
    });
  }

  #update(): components['schemas']['RoundGamesState'] | undefined {
    if (!this.games) return;

    this.snapshot = {
      timestampMs: Date.now(),
      eventType: 'RoundGamesStateShort',
      data: Object.values(this.games),
    };
    this.hasUnfinishedGames = !!this.snapshot.data.find((g) => g.status !== 'Finished');
  }

  get subscribers(): number {
    return Math.max(this.ws.subscribers - 1, 0);
  }

  discard() {
    this.ws.discard();
  }
}

// Держать стор тура живым: создать при первом обращении, отпустить, когда никто не держит
function holdRoundStore(tournamentId: string, roundNumber: number, ql: QueryClient): () => void {
  const key = `${tournamentId}/${roundNumber}`;
  if (!globalRoundWs[key]) {
    globalRoundWs[key] = new RoundShortGamesSotre(
      new RoundShortGamesSocket(tournamentId, roundNumber),
      ql,
    );
  }
  const store = globalRoundWs[key];
  const unsubscribe = store.ws.subscribe(FUNC_NOOP);
  return () => {
    unsubscribe();
    if (store.subscribers === 0) {
      store.discard();
      delete globalRoundWs[key];
    }
  };
}
